/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { IQueueNavigationBinding } from 'app/core/models/essette-interfaces';
import { AuthService } from 'app/um/services/auth-service';
import { apiCall } from 'app/shared/lib/api/api';
import { Toaster } from 'app/shared/lib/toaster';
import * as UM from 'app/um/models/essette-api';
import { guid } from 'app/shared/lib/guid';


@autoinject()
export class RoutingQueueNavigation extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    private authId!: string;
    private queueTypeCode!: string;
    private queueId!: string;
    private isReturnToQueueOnly: boolean = false;

    private queue: UM.AuthQueue | null = null;
    private allAuths: ReadonlyArray<UM.Auth> | null = null;

    protected currentAuth: UM.Auth | null = null;
    protected nextAuth: UM.Auth | null = null;
    protected previousAuth: UM.Auth | null = null;
    protected id: guid = guid();

    ////****************** Element References ***************************************////



    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);

    }

    protected async activate({ id, queueTypeCode, queueId }: IQueueNavigationBinding): Promise<void> {
        if(!id) { throw new Error('Missing required property: [id]'); }
        if(!queueId) { throw new Error('Missing required property: [queueId]'); }
        if(id === this.authId && queueTypeCode === this.queueTypeCode) { return; }

        this.authId = id;
        this.queueTypeCode = queueTypeCode;
        this.queueId = queueId;

        this.isReturnToQueueOnly = this.queueTypeCode === 'UM.Portal';

        this.reload();
    }
    protected deactivate(): void { }


    ////****************** Events ***************************************************////



    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        if(!this.authId) { throw new Error('Missing required property: [authId]'); }
        if(!this.queueId) { throw new Error('Missing required property: [queueId]'); }

        if(!this.isReturnToQueueOnly) {
            if(!this.queue || this.queue.id !== this.queueId) {
                this.loadQueue(this.queueId);
            }

            if(!this.allAuths) {
                await this.loadQueueItems(this.queueId);
            }

            this.setQueuePosition(this.authId);
        }
    }

    /** Finds the current auth in the array and sets the next and previous auth */
    private setQueuePosition(id: string): void {
        if(id && this.allAuths) {
            const index = this.allAuths.findIndex(a => a.id === id);
            if(index === -1) {
                //this.showNavigation = false;
                return;
            }
            this.currentAuth = this.allAuths[index];
            const isLastAuth = index === this.allAuths.length - 1;
            this.nextAuth = isLastAuth ? null : this.allAuths[index + 1];
        }
    }

    /** Navigates user to an item id, optionally returning auth from it's queue first */
    protected async getNext(nextAuthId: string | null, returnFromQueue: boolean = false): Promise<void> {
        if(returnFromQueue) {
            const success = await this.returnFromQueue(this.authId, this.queueId);
            if(!success) { return; }
        }

        if(!!nextAuthId) {
            const { queueTypeCode, queueId } = this;
            this.router.navigateToRoute('um.auth', { authId: nextAuthId, queueTypeCode, queueId })

            // Removes focus from button (so it stops flashing after navigating)
            const activeElement: HTMLElement | null = <HTMLElement>document.activeElement;
            if(activeElement && activeElement.blur) { activeElement.blur(); }
        }
        else {
            this.returnToQueue();
        }
    }

    private async returnToQueue(): Promise<void> {
        switch(this.queueTypeCode) {
            case 'UM.Portal':
                this.router.navigate(`#/um/queues/portal/${this.queueId}`);
                break;
            case 'UM.Routing':
            default:
                this.router.navigate(`#/um/queues/auth/${this.queueId}`);
        }

        return;
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadQueue(queueId: string): Promise<void> {
        try {
            const response = await this.service.loadQueue(queueId);
            this.queue = response.results[0];
        } catch(error) {
            throw error;
        }
    }

    @apiCall
    private async loadQueueItems(queueId: string): Promise<void> {
        // Default sort for auth queues
        const sort = new Sorting();
        sort.entries = [
            {
                reference: 'priority.sortOrder',
                direction: SortDirection.Ascending
            },
            {
                reference: 'number',
                direction: SortDirection.Ascending
            }
        ];

        try {
            this.state.beginLoad();
            const query = this.service.createLoadAuthsByQueueQuery(queueId, { all: true });
            query.applySorting(sort);
            const response = await this.service.get<UM.Auth>(query);
            this.allAuths = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async returnFromQueue(authId: string, queueId: string): Promise<boolean> {
        try {
            this.state.beginSave();
            const response = await this.service.returnFromQueue(authId, queueId);
            this.toaster.standard.returned('um:auth.$', response.isSuccessful);
            return response.isSuccessful;
        } catch(error) {
            this.toaster.standard.returned('um:auth.$', false);
            this.logger.error(`Unable to return auth from queue`, error);
            return false;
        } finally {
            this.state.endSave();
        }
    }

}
